export class AlertModal {
    constructor(title, message, onConfirm = null) {
        this.title = title;
        this.message = message;
        this.onConfirm = onConfirm;
    }

    render() {
        this.cleanup();

        // 1. Overlay
        this.overlay = document.createElement('div');
        this.overlay.className = "fixed inset-0 bg-black/50 z-[9990] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-300 opacity-0";

        // 2. Modal Content
        this.modal = document.createElement('div');
        this.modal.className = "bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 transform scale-95 transition-all duration-300 opacity-0 border border-zinc-100 dark:border-zinc-800";

        this.modal.innerHTML = `
            <div class="mb-6">
                <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">${this.title}</h3>
                <div class="text-sm text-zinc-500 leading-relaxed">${this.message}</div>
            </div>
            
            <button id="btn-confirm" class="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm transition-colors shadow-lg shadow-violet-500/30">
                확인
            </button>
        `;

        this.overlay.appendChild(this.modal);
        document.body.appendChild(this.overlay);

        // Event Listeners
        const btnConfirm = this.modal.querySelector('#btn-confirm');

        const closeAndConfirm = () => {
            if (this.onConfirm) this.onConfirm();
            this.close();
        };

        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) closeAndConfirm();
        });

        btnConfirm.onclick = closeAndConfirm;

        // Enter Key
        document.addEventListener('keyup', this.handleKeyUp); // Need to bind/unbind

        // Animation In
        requestAnimationFrame(() => {
            this.overlay.classList.remove('opacity-0');
            this.modal.classList.remove('opacity-0', 'scale-95');
            this.modal.classList.add('scale-100');
        });
    }

    handleKeyUp = (e) => {
        if (e.key === 'Enter') {
            const btn = this.modal?.querySelector('#btn-confirm');
            btn?.click();
        }
    }

    close() {
        document.removeEventListener('keyup', this.handleKeyUp);
        if (this.overlay) {
            this.overlay.classList.add('opacity-0');
            this.modal.classList.add('opacity-0', 'scale-95');
            setTimeout(() => {
                this.cleanup();
            }, 300);
        }
    }

    cleanup() {
        if (this.overlay) this.overlay.remove();
    }

    /**
     * Static Toast Helper (Restored)
     */
    static toast(message, title = "알림", duration = 3000) {
        const toastEl = document.createElement('div');
        toastEl.className = "fixed bottom-10 left-1/2 transform -translate-x-1/2 bg-zinc-800 text-white px-6 py-3 rounded-full shadow-2xl z-[10000] flex items-center gap-3 border border-zinc-700 animate-slide-up";
        toastEl.style.cssText = "font-family: 'Noto Sans KR', sans-serif; transition: all 0.3s ease;";

        toastEl.innerHTML = `
            <div class="flex flex-col">
                ${title ? `<span class="text-[10px] text-zinc-400 font-bold uppercase tracking-wider">${title}</span>` : ''}
                <span class="text-sm font-medium">${message}</span>
            </div>
        `;

        document.body.appendChild(toastEl);

        // Animate In
        requestAnimationFrame(() => {
            toastEl.classList.remove('translate-y-4', 'opacity-0');
        });

        // Remove
        setTimeout(() => {
            toastEl.style.opacity = '0';
            toastEl.style.transform = 'translate(-50%, 20px)';
            setTimeout(() => toastEl.remove(), 300);
        }, duration);
    }
}

import { InputModal } from "./InputModal.js";

// [Global Access]
// Ensure window.Modal is available if not already
if (!window.Modal) {
    window.Modal = {
        alert: (msg, title) => new AlertModal(title || "알림", msg).render(),
        confirm: (msg, title) => new Promise(resolve => {
            // ConfirmModal Implementation required, or basic confirm
            // For now, mapping toast.
        }),
        prompt: (title, value, onConfirm) => new InputModal(title, value, onConfirm).render(),
        toast: AlertModal.toast
    };
} else {
    // Append methods if missing
    window.Modal.toast = AlertModal.toast;
    window.Modal.prompt = (title, value, onConfirm) => new InputModal(title, value, onConfirm).render();
}
